import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  getCurrentUser, 
  logout as logoutService, 
  setToken,
  getSecureHeaders,
  getCsrfToken 
} from '../services/auth.service';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * SECURITY: Auth provider with HttpOnly cookie support
 * - Primary auth via HttpOnly cookie (set by backend)
 * - Falls back to localStorage for backward compatibility
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * SECURITY: Fetch user from backend using HttpOnly cookie auth
   */
  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * SECURITY: Logout with backend call to clear HttpOnly cookie
   */
  const logout = useCallback(async () => {
    try {
      await logoutService();
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
      setUser(null);
    }
  }, []);

  /**
   * Login with token from OAuth callback
   * SECURITY: Token is also set in HttpOnly cookie by backend
   */
  const login = useCallback(async (token) => {
    if (token) {
      setToken(token); // Store in localStorage as fallback
    }
    await fetchUser();
  }, [fetchUser]);

  const refetchUser = useCallback(() => {
    return fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value = {
    user,
    loading,
    logout,
    login,
    refetchUser,
    isAuthenticated: !!user,
    // SECURITY: Provide secure headers for API calls
    getSecureHeaders,
    csrfToken: getCsrfToken()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

